/**
 * The retained UI tree of one canvas: owns the layout engine nodes, stylesheets, dirty tracking
 * and the per-frame update (resolve styles → layout → absolute rects). The Vue host ops call the
 * mutation methods; the render pass and input system read the resulting geometry.
 */
import { UIMeasureMode, type ILayoutEngine, type ILayoutNode } from '../layout/ILayoutEngine';
import { EASING, canInterpolate, lerpValue } from '../style/interpolate';
import { parseInlineStyle } from '../style/parse';
import { layoutChanged, resolveStyle, textMeasureChanged, valueEq } from '../style/resolve';
import { DEFAULT_TEXT_STYLE, MonospaceTextMeasurer, type ITextMeasurer, type UITextStyle } from '../text/TextMeasurer';
import {
  LAYOUT_KEYS,
  type AnimationDecl,
  type Dim,
  type Keyframes,
  type PseudoState,
  type StyleDecls,
  type StyleSheetData,
  type TransitionDecl,
} from '../types';
import { UINode, UI_ELEMENT_TYPES, type AnimationInstance, type UIElementType } from './UINode';

const LAYOUT_KEY_SET = new Set<keyof StyleDecls>(LAYOUT_KEYS);

/** Last transition entry whose `prop` matches the key (or 'all'); null when none applies. */
function matchTransition(list: readonly TransitionDecl[], key: keyof StyleDecls): TransitionDecl | null {
  let match: TransitionDecl | null = null;
  for (const t of list) if (t.prop === key || t.prop === 'all') match = t;
  return match;
}

/** Dim-typed style keys — the ones unit resolution (em/vw/vh/calc → px|%) applies to. */
const DIM_KEYS = [
  'width',
  'height',
  'minWidth',
  'minHeight',
  'maxWidth',
  'maxHeight',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'flexBasis',
  'top',
  'right',
  'bottom',
  'left',
] as const satisfies readonly (keyof StyleDecls)[];

export interface UIDocumentOptions {
  measurer?: ITextMeasurer;
}

export class UIDocument {
  readonly root: UINode;
  measurer: ITextMeasurer;

  private readonly engine: ILayoutEngine;
  private readonly sheets: StyleSheetData[] = [];
  private readonly styleDirty = new Set<UINode>();
  /** Nodes with in-flight transitions (UI-18) or animations (UI-18b) — ticked every update(). */
  private readonly animating = new Set<UINode>();
  /** Merged `@keyframes` tracks across all sheets (UI-18b), by name. */
  private readonly keyframesByName = new Map<string, Keyframes>();
  private readonly warnedMissingKeyframes = new Set<string>();
  private layoutRequested = true;
  private warnedTextInView = false;
  private warnedCalcMixedPct = false;
  /** Any resolved style used vw/vh — a viewport resize must then restyle the whole tree. */
  private usesViewportUnits = false;

  viewportWidth = 0;
  viewportHeight = 0;
  /** UI clock in seconds (accumulated from update(dt)) — the seam transitions/animations (UI-18) interpolate against. */
  time = 0;

  constructor(engine: ILayoutEngine, options?: UIDocumentOptions) {
    this.engine = engine;
    this.measurer = options?.measurer ?? new MonospaceTextMeasurer();
    this.root = new UINode('root', null, engine.createNode());
  }

  // -------------------------------------------------------------------------
  // stylesheets / viewport

  addStyleSheet(sheet: StyleSheetData): void {
    this.sheets.push(sheet);
    if (sheet.keyframes) {
      for (const [name, kf] of Object.entries(sheet.keyframes)) this.keyframesByName.set(name, kf);
    }
    this.invalidateAllStyles(this.root);
  }

  setViewport(width: number, height: number): void {
    if (width === this.viewportWidth && height === this.viewportHeight) return;
    this.viewportWidth = width;
    this.viewportHeight = height;
    this.root.layout!.applyStyle({ width, height });
    this.layoutRequested = true;
    // vw/vh values were baked to px at style-apply — a resize must re-resolve them
    if (this.usesViewportUnits) this.invalidateAllStyles(this.root);
  }

  private invalidateAllStyles(node: UINode): void {
    if (node.isElement) this.styleDirty.add(node);
    for (const c of node.children) this.invalidateAllStyles(c);
  }

  // -------------------------------------------------------------------------
  // node factory (Vue host ops)

  createElement(type: string): UINode {
    if (!UI_ELEMENT_TYPES.has(type)) {
      throw new Error(`[ui] Unknown element <${type}> — primitives are view/text/image/button`);
    }
    const node = new UINode('element', type as UIElementType, this.engine.createNode());
    if (type === 'text') this.wireTextMeasure(node);
    this.styleDirty.add(node);
    return node;
  }

  createRawText(text: string): UINode {
    const node = new UINode('rawtext', null, null);
    node.text = text;
    return node;
  }

  createComment(): UINode {
    return new UINode('comment', null, null);
  }

  // -------------------------------------------------------------------------
  // tree mutations (Vue host ops)

  insert(child: UINode, parent: UINode, anchor: UINode | null = null): void {
    if (child.parent) this.detach(child);
    const index = anchor ? parent.children.indexOf(anchor) : parent.children.length;
    parent.children.splice(index < 0 ? parent.children.length : index, 0, child);
    child.parent = parent;

    if (child.layout && parent.layout) {
      if (parent.elementType === 'text') {
        // elements nested inside <text> are inert in v1 (rich text is v2)
        if (!this.warnedTextInView) {
          this.warnedTextInView = true;
          console.warn('[ui] elements inside <text> are not laid out (v1)');
        }
      } else {
        parent.layout.insertChild(child.layout, this.layoutIndexOf(parent, child));
        this.layoutRequested = true;
      }
    }
    if (child.kind === 'rawtext') this.onTextChanged(parent);
    // the whole subtree re-resolves: descendant selectors depend on the NEW ancestry
    if (child.isElement) this.invalidateAllStyles(child);
  }

  remove(child: UINode): void {
    const parent = child.parent;
    this.detach(child);
    if (parent && child.kind === 'rawtext') this.onTextChanged(parent);
    this.freeSubtree(child);
  }

  private detach(child: UINode): void {
    const parent = child.parent;
    if (!parent) return;
    const i = parent.children.indexOf(child);
    if (i >= 0) parent.children.splice(i, 1);
    child.parent = null;
    if (child.layout && parent.layout && parent.elementType !== 'text') {
      parent.layout.removeChild(child.layout);
      this.layoutRequested = true;
    }
  }

  private layoutIndexOf(parent: UINode, child: UINode): number {
    let idx = 0;
    for (const c of parent.children) {
      if (c === child) return idx;
      if (c.layout) idx++;
    }
    return idx;
  }

  private freeSubtree(node: UINode): void {
    // depth-first; each child's layout node must be DETACHED from its parent before free()
    // (freeing a still-linked yoga node corrupts the parent's child list)
    for (const c of [...node.children]) {
      if (c.layout && node.layout && node.elementType !== 'text') node.layout.removeChild(c.layout);
      this.freeSubtree(c);
    }
    this.styleDirty.delete(node);
    this.animating.delete(node);
    node.tweens = null;
    node.animations = null;
    node.animBase = null;
    node.layout?.free();
  }

  // -------------------------------------------------------------------------
  // text

  setRawText(node: UINode, text: string): void {
    if (node.text === text) return;
    node.text = text;
    if (node.parent) this.onTextChanged(node.parent);
  }

  setElementText(el: UINode, text: string): void {
    for (const c of [...el.children]) this.remove(c);
    el.text = text;
    this.onTextChanged(el);
  }

  private onTextChanged(from: UINode): void {
    const textEl = from.closestTextElement();
    if (textEl?.layout) {
      textEl.layout.markDirty();
      this.layoutRequested = true;
    }
  }

  private wireTextMeasure(node: UINode): void {
    node.layout!.setMeasureFunc((width, widthMode) => {
      const style = this.effectiveTextStyle(node);
      return this.measurer.measure(node.textContent(), style, width, widthMode);
    });
  }

  /** Inherited lookup: nearest ancestor (inclusive) that sets the key. */
  inherited<K extends keyof StyleDecls>(node: UINode, key: K): StyleDecls[K] | undefined {
    let n: UINode | null = node;
    while (n) {
      const v = n.style[key];
      if (v !== undefined) return v;
      n = n.parent;
    }
    return undefined;
  }

  effectiveTextStyle(node: UINode): UITextStyle {
    return {
      font: this.inherited(node, 'font') ?? DEFAULT_TEXT_STYLE.font,
      fontFamily: this.inherited(node, 'fontFamily') ?? DEFAULT_TEXT_STYLE.fontFamily,
      fontWeight: this.inherited(node, 'fontWeight') ?? DEFAULT_TEXT_STYLE.fontWeight,
      fontStyle: this.inherited(node, 'fontStyle') ?? DEFAULT_TEXT_STYLE.fontStyle,
      fontSize: this.inherited(node, 'fontSize') ?? DEFAULT_TEXT_STYLE.fontSize,
      lineHeight: this.inherited(node, 'lineHeight') ?? DEFAULT_TEXT_STYLE.lineHeight,
      letterSpacing: this.inherited(node, 'letterSpacing') ?? DEFAULT_TEXT_STYLE.letterSpacing,
      textAlign: this.inherited(node, 'textAlign') ?? DEFAULT_TEXT_STYLE.textAlign,
      whiteSpace: this.inherited(node, 'whiteSpace') ?? DEFAULT_TEXT_STYLE.whiteSpace,
    };
  }

  // -------------------------------------------------------------------------
  // props / states

  patchProp(node: UINode, key: string, next: unknown): void {
    if (key === 'class') {
      node.classes = next ? String(next).trim().split(/\s+/).filter(Boolean) : [];
      this.invalidateAllStyles(node); // descendants may match `.newclass .x` rules now
      return;
    }
    if (key === 'style') {
      node.inline = next ? parseInlineStyle(next as string | Record<string, unknown>) : null;
      this.styleDirty.add(node);
      return;
    }
    if (/^on[A-Z]/.test(key)) {
      const event = key.slice(2).toLowerCase();
      if (next == null) node.handlers.delete(event);
      else node.handlers.set(event, next);
      return;
    }
    if (key === 'disabled') {
      this.setState(node, 'disabled', Boolean(next));
      return;
    }
    if (next == null) delete node.attrs[key];
    else node.attrs[key] = next;
  }

  setState(node: UINode, state: PseudoState, on: boolean): void {
    const had = node.states.has(state);
    if (had === on) return;
    if (on) node.states.add(state);
    else node.states.delete(state);
    // subtree, not just the node: `.slot:hover .icon` rules re-match on descendants
    this.invalidateAllStyles(node);
  }

  // -------------------------------------------------------------------------
  // per-frame update

  /** Resolve dirty styles → advance transitions → run layout if needed → refresh absolute rects.
   *  Returns true if geometry changed (paint-only transitions animate without it — UIPass repaints
   *  every frame reading node.style). */
  update(dt = 0): boolean {
    this.time += dt;
    if (this.styleDirty.size) {
      for (const node of this.styleDirty) {
        if (!node.isConnected()) continue;
        this.applyResolvedStyle(node);
      }
      this.styleDirty.clear();
    }
    if (this.animating.size) this.tickAnimated();

    if (!this.layoutRequested) return false;
    this.layoutRequested = false;

    this.root.layout!.calculateLayout(this.viewportWidth, this.viewportHeight);
    this.refreshRects(this.root, 0, 0);
    return true;
  }

  private applyResolvedStyle(node: UINode): void {
    const prev = node.style;
    const target = this.resolveUnits(resolveStyle(this.sheets, node, node.inline), node);

    // --- animations (UI-18b) take over the node's displayed style whenever any resolve to a track ---
    const animDecls = target.animation ?? null;
    if (animDecls && animDecls.length) this.reconcileAnimations(node, animDecls);
    else if (node.animations) this.clearAnimations(node);

    if (node.animations) {
      node.animBase = target;
      if (node.tweens) node.tweens = null; // an animation supersedes transitions on the same node
      this.commitStyle(node, prev, this.sampleAnimations(node, target, this.time));
      this.animating.add(node);
      return;
    }

    const transitions = target.transition ?? null;

    if (!transitions || transitions.length === 0) {
      if (node.tweens) {
        node.tweens = null;
        this.animating.delete(node);
      }
      this.commitStyle(node, prev, target);
      return;
    }

    // Diff current-displayed (prev) vs target; transitioned+interpolatable props tween from the
    // current value, everything else applies immediately. `next` is what we display THIS frame.
    const next: StyleDecls = { ...target };
    const keys = new Set<keyof StyleDecls>([
      ...(Object.keys(prev) as (keyof StyleDecls)[]),
      ...(Object.keys(target) as (keyof StyleDecls)[]),
    ]);
    for (const key of keys) {
      const from = prev[key];
      const to = target[key];
      if (valueEq(from, to)) continue;
      const cfg = matchTransition(transitions, key);
      if (cfg && cfg.durationMs > 0 && canInterpolate(key, from, to)) {
        this.startTween(node, key, from, to, cfg);
        (next as Record<string, unknown>)[key] = from; // hold current until the tween ticks
      } else {
        node.tweens?.delete(key); // an untransitioned change cancels a prior tween on this key
      }
    }
    this.commitStyle(node, prev, next);
    if (node.tweens?.size) this.animating.add(node);
  }

  /** Apply a freshly-resolved style bag to a node, propagating layout / text-measure invalidation. */
  private commitStyle(node: UINode, prev: StyleDecls, next: StyleDecls): void {
    node.style = next;
    if (node.layout && layoutChanged(prev, next)) {
      node.layout.applyStyle(next);
      this.layoutRequested = true;
    }
    if (textMeasureChanged(prev, next)) {
      // inherited text props: any text element in this subtree may re-measure
      this.markTextDirtyDeep(node);
    }
  }

  private startTween(node: UINode, key: keyof StyleDecls, from: unknown, to: unknown, cfg: TransitionDecl): void {
    (node.tweens ??= new Map()).set(key, {
      from,
      to,
      start: this.time,
      delaySec: cfg.delayMs / 1000,
      durSec: cfg.durationMs / 1000,
      easing: cfg.easing,
    });
  }

  /** Advance every animating node against the clock: re-sample CSS animations, step transitions. */
  private tickAnimated(): void {
    for (const node of this.animating) {
      if (!node.isConnected()) {
        this.clearAnimations(node);
        node.tweens = null;
        this.animating.delete(node);
        continue;
      }
      let layoutTouched = false;

      // CSS animations (UI-18b): recompute the displayed style from the base every frame.
      if (node.animations && node.animBase) {
        const displayed = this.sampleAnimations(node, node.animBase, this.time);
        if (node.layout && layoutChanged(node.style, displayed)) layoutTouched = true;
        node.style = displayed;
        // once every animation has settled (finite + past end) the frame is stable — stop ticking.
        if (!node.animations.some((a) => this.animationActive(a, this.time)) && !node.tweens?.size) {
          this.animating.delete(node);
        }
      }

      // Transitions (UI-18): a node never has both (an animation clears its tweens), but tick both.
      const tweens = node.tweens;
      if (tweens && tweens.size) {
        const next: StyleDecls = { ...node.style };
        for (const [key, tw] of tweens) {
          const elapsed = this.time - tw.start - tw.delaySec;
          if (elapsed < 0) continue; // still in the delay window
          const p = tw.durSec <= 0 ? 1 : Math.min(1, elapsed / tw.durSec);
          (next as Record<string, unknown>)[key] =
            p >= 1 ? tw.to : lerpValue(key, tw.from, tw.to, EASING[tw.easing](p));
          if (p >= 1) tweens.delete(key);
          if (LAYOUT_KEY_SET.has(key)) layoutTouched = true;
        }
        node.style = next;
        if (tweens.size === 0) {
          node.tweens = null;
          if (!node.animations) this.animating.delete(node);
        }
      }

      if (layoutTouched && node.layout) {
        node.layout.applyStyle(node.style);
        this.layoutRequested = true;
      }
      // drop nodes that have shed both animations and tweens (e.g. `animation` removed by a class change)
      if (!node.animations && !node.tweens?.size) this.animating.delete(node);
    }
  }

  // -------------------------------------------------------------------------
  // CSS animations (UI-18b) — keyframe tracks sampled over the doc clock, layered over the base

  private clearAnimations(node: UINode): void {
    node.animations = null;
    node.animBase = null;
  }

  /** Reconcile the node's running instances against its resolved `animation` list. Instances whose
   *  decl is unchanged keep playing (preserve `start`); new/changed ones (re)start from now. */
  private reconcileAnimations(node: UINode, decls: readonly AnimationDecl[]): void {
    const prevByName = new Map<string, AnimationInstance>();
    if (node.animations) for (const inst of node.animations) prevByName.set(inst.decl.name, inst);
    const next: AnimationInstance[] = [];
    for (const decl of decls) {
      const track = this.keyframesByName.get(decl.name);
      if (!track) {
        if (!this.warnedMissingKeyframes.has(decl.name)) {
          this.warnedMissingKeyframes.add(decl.name);
          console.warn(`[ui] @keyframes '${decl.name}' not found`);
        }
        continue;
      }
      const existing = prevByName.get(decl.name);
      if (existing && valueEq(existing.decl, decl)) next.push(existing);
      else next.push({ decl, track, start: this.time });
    }
    if (next.length) node.animations = next;
    else this.clearAnimations(node); // every referenced track was missing
  }

  /** Base style with every running animation's current sample layered on top (last animation wins). */
  private sampleAnimations(node: UINode, base: StyleDecls, time: number): StyleDecls {
    const out: StyleDecls = { ...base };
    if (!node.animations) return out;
    for (const inst of node.animations) {
      const sample = this.sampleAnimation(inst, base, time);
      if (sample) Object.assign(out, sample);
    }
    return out;
  }

  /** One animation's override at `time` (null = no override this frame — outside an unfilled window). */
  private sampleAnimation(inst: AnimationInstance, base: StyleDecls, time: number): Partial<StyleDecls> | null {
    const { decl, track } = inst;
    const dur = decl.durationMs / 1000;
    const elapsed = time - inst.start - decl.delayMs / 1000;
    const iters = decl.infinite ? Infinity : decl.iterations;
    const fills = (side: 'start' | 'end') =>
      decl.fillMode === 'both' || decl.fillMode === (side === 'start' ? 'backwards' : 'forwards');

    if (elapsed < 0) return fills('start') ? this.sampleTrack(track, base, this.dirProgress(decl, 0, 0), decl.easing) : null;
    if (dur <= 0 || iters <= 0) {
      return fills('end')
        ? this.sampleTrack(track, base, this.dirProgress(decl, Math.max(0, Math.ceil(iters) - 1), 1), decl.easing)
        : null;
    }
    const rawIter = elapsed / dur;
    if (rawIter >= iters) {
      return fills('end') ? this.sampleTrack(track, base, this.dirProgress(decl, Math.ceil(iters) - 1, 1), decl.easing) : null;
    }
    const iterIndex = Math.floor(rawIter);
    return this.sampleTrack(track, base, this.dirProgress(decl, iterIndex, rawIter - iterIndex), decl.easing);
  }

  /** Map an iteration's local fraction to a track position, honoring `direction` (alternate flips odd). */
  private dirProgress(decl: AnimationDecl, iterIndex: number, frac: number): number {
    let reversed = false;
    if (decl.direction === 'reverse') reversed = true;
    else if (decl.direction === 'alternate') reversed = iterIndex % 2 === 1;
    else if (decl.direction === 'alternate-reverse') reversed = iterIndex % 2 === 0;
    return reversed ? 1 - frac : frac;
  }

  /** Sample every animated prop at track position `p` (0..1), interpolating within the enclosing
   *  segment (missing 0/1 endpoints fall back to the base value; non-interpolatable props step). */
  private sampleTrack(track: Keyframes, base: StyleDecls, p: number, easing: AnimationDecl['easing']): Partial<StyleDecls> {
    const out: Record<string, unknown> = {};
    const ease = EASING[easing];
    for (const prop of track.props) {
      const pts: { pos: number; val: unknown }[] = [];
      for (const stop of track.stops) {
        const v = stop.decls[prop];
        if (v !== undefined) pts.push({ pos: stop.pos, val: v });
      }
      if (!pts.length) continue;
      if (pts[0]!.pos > 0 && base[prop] !== undefined) pts.unshift({ pos: 0, val: base[prop] });
      if (pts[pts.length - 1]!.pos < 1 && base[prop] !== undefined) pts.push({ pos: 1, val: base[prop] });
      if (p <= pts[0]!.pos) {
        out[prop] = pts[0]!.val;
        continue;
      }
      if (p >= pts[pts.length - 1]!.pos) {
        out[prop] = pts[pts.length - 1]!.val;
        continue;
      }
      let i = 0;
      while (i < pts.length - 1 && p > pts[i + 1]!.pos) i++;
      const lo = pts[i]!;
      const hi = pts[i + 1]!;
      const span = hi.pos - lo.pos;
      const segFrac = span > 0 ? (p - lo.pos) / span : 0;
      out[prop] = canInterpolate(prop, lo.val, hi.val) ? lerpValue(prop, lo.val, hi.val, ease(segFrac)) : lo.val;
    }
    return out as Partial<StyleDecls>;
  }

  /** Whether an instance still changes over time (needs ticking): infinite, or before its end. */
  private animationActive(inst: AnimationInstance, time: number): boolean {
    if (inst.decl.infinite) return true;
    const end = inst.start + inst.decl.delayMs / 1000 + (inst.decl.durationMs / 1000) * inst.decl.iterations;
    return time < end;
  }

  /**
   * Bake em/vw/vh/calc dims down to px | {pct} — the layout engine only understands those.
   * em = the node's effective font size (own decl or inherited); vw/vh = % of the LOGICAL
   * viewport. calc() mixing % with absolute units drops the % part (warned once): the parent
   * size isn't known before layout.
   */
  private resolveUnits(decls: StyleDecls, node: UINode): StyleDecls {
    let out: StyleDecls | null = null;
    for (const key of DIM_KEYS) {
      const v = decls[key] as Dim | undefined;
      if (v === undefined || typeof v !== 'object' || !('calc' in v)) continue;
      const c = v.calc;
      if (c.vw || c.vh) this.usesViewportUnits = true;
      if (c.pct && !this.warnedCalcMixedPct) {
        this.warnedCalcMixedPct = true;
        console.warn('[ui] calc() mixing % with absolute units is unsupported — % part ignored');
      }
      const fontSize = c.em
        ? (decls.fontSize ??
          (node.parent ? this.inherited(node.parent, 'fontSize') : undefined) ??
          DEFAULT_TEXT_STYLE.fontSize)
        : 0;
      const px =
        (c.px ?? 0) +
        ((c.vw ?? 0) / 100) * this.viewportWidth +
        ((c.vh ?? 0) / 100) * this.viewportHeight +
        (c.em ?? 0) * fontSize;
      out ??= { ...decls };
      (out as Record<string, unknown>)[key] = px;
    }
    return out ?? decls;
  }

  /** Invalidate every text measure — e.g. a font finished loading and real metrics arrived. */
  remeasureText(): void {
    this.markTextDirtyDeep(this.root);
  }

  private markTextDirtyDeep(node: UINode): void {
    if (node.elementType === 'text' && node.layout) {
      node.layout.markDirty();
      this.layoutRequested = true;
    }
    for (const c of node.children) this.markTextDirtyDeep(c);
  }

  private refreshRects(node: UINode, parentX: number, parentY: number): void {
    let x = parentX;
    let y = parentY;
    if (node.layout) {
      x = parentX + node.layout.left;
      y = parentY + node.layout.top;
      node.layoutX = x;
      node.layoutY = y;
      node.layoutW = node.layout.width;
      node.layoutH = node.layout.height;
      node.layout.markLayoutSeen();
    }
    let childX = x;
    let childY = y;
    if (node.style.overflow === 'scroll') {
      // content extent from the children's OWN layout rects (relative coords — scroll-independent)
      let cw = 0;
      let ch = 0;
      for (const c of node.children) {
        if (!c.layout) continue;
        cw = Math.max(cw, c.layout.left + c.layout.width);
        ch = Math.max(ch, c.layout.top + c.layout.height);
      }
      node.contentW = cw;
      node.contentH = ch;
      node.scrollX = Math.min(Math.max(0, node.scrollX), Math.max(0, cw - node.layoutW));
      node.scrollY = Math.min(Math.max(0, node.scrollY), Math.max(0, ch - node.layoutH));
      childX -= node.scrollX;
      childY -= node.scrollY;
    }
    for (const c of node.children) this.refreshRects(c, childX, childY);
  }

  /**
   * Scroll an `overflow: scroll` container by a delta (logical px), clamped to its content.
   * Returns true when the offset changed (absolute rects refresh on the next update()).
   */
  scrollBy(node: UINode, dx: number, dy: number): boolean {
    if (node.style.overflow !== 'scroll') return false;
    const nx = Math.min(Math.max(0, node.scrollX + dx), Math.max(0, node.contentW - node.layoutW));
    const ny = Math.min(Math.max(0, node.scrollY + dy), Math.max(0, node.contentH - node.layoutH));
    if (nx === node.scrollX && ny === node.scrollY) return false;
    node.scrollX = nx;
    node.scrollY = ny;
    this.layoutRequested = true;
    return true;
  }

  // -------------------------------------------------------------------------

  dispose(): void {
    for (const c of [...this.root.children]) this.remove(c);
    this.root.layout?.free();
    this.styleDirty.clear();
    this.animating.clear();
  }
}
